import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Flame, Award, Bell, Palette, Grid3x3, Type, Sparkles, Zap, LogIn, HelpCircle, Building2, Camera, Pencil, Trophy, Filter, Store } from "lucide-react";
import { api, API } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

function HubLeaderboard({ token, me }) {
  const GAMES = [["all","All"],["rebus","Rebus"],["sudoku","Sudoku"],["word","Word"],["trivia","Trivia"]];
  const [game, setGame] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = `token=${encodeURIComponent(token || "demo")}&game=${game}` + (start ? `&start=${start}` : "") + (end ? `&end=${end}` : "");
    api.get(`/employee/global-leaderboard?${qs}`)
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [game, start, end, token]);

  return (
    <div className="brutal-card p-6 mt-10" data-testid="hub-leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-xl font-black uppercase flex items-center gap-2"><Trophy size={20}/> Leaderboard</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-xs uppercase font-mono-bq" style={{color:"var(--bq-muted)"}}><Filter size={12}/> Game</div>
          <div className="flex flex-wrap gap-1" data-testid="lb-game-filter">
            {GAMES.map(([k,label]) => (
              <button key={k} data-testid={`lb-game-${k}`} onClick={()=>setGame(k)} className={`px-2 py-1 border-2 themed-border text-xs font-black uppercase ${game===k ? "themed-shadow-accent" : ""}`} style={{background: game===k ? "var(--bq-accent)" : "var(--bq-card-bg)"}}>{label}</button>
            ))}
          </div>
          <input data-testid="lb-start-date" type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="border-2 themed-border px-2 py-1 text-xs" style={{background:"var(--bq-card-bg)"}} title="From date"/>
          <input data-testid="lb-end-date" type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="border-2 themed-border px-2 py-1 text-xs" style={{background:"var(--bq-card-bg)"}} title="To date"/>
          {(start || end) && <button data-testid="lb-clear-dates" onClick={()=>{setStart("");setEnd("");}} className="px-2 py-1 border-2 themed-border text-xs font-black uppercase">Clear</button>}
        </div>
      </div>
      {loading ? (
        <p className="text-sm" style={{color:"var(--bq-muted)"}}>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm" data-testid="lb-empty" style={{color:"var(--bq-muted)"}}>No scores for this filter yet — be the first to play!</p>
      ) : (
        <ol className="grid gap-2" data-testid="lb-rows">
          {rows.map((r, i) => (
            <li key={r.user_id} data-testid={`lb-row-${i}`} className="flex items-center gap-3 border-2 themed-border p-2" style={{background: r.employee_id === me?.employee_id ? "var(--bq-accent2)" : "var(--bq-card-bg)"}}>
              <div className="w-8 h-8 border-2 themed-border inline-flex items-center justify-center font-black">{i+1}</div>
              {r.avatar_path ? <img src={`${API}/files/${r.avatar_path}`} alt="" className="w-8 h-8 object-cover border-2 themed-border"/> : null}
              <div className="flex-1 min-w-0 truncate font-bold">{r.name}{r.department ? <span className="text-xs font-normal" style={{color:"var(--bq-muted)"}}> · {r.department}</span> : null}</div>
              <div className="font-mono-bq font-black">{r.points} pts</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const GAMES = [
  { key: "rebus", path: "/rebus", icon: Brain, title: "Rebus Daily", desc: "Crack the picture-word puzzle before the timer ends.", color: "var(--bq-danger)" },
  { key: "sudoku", path: "/sudoku", icon: Grid3x3, title: "Sudoku Daily", desc: "Fill the 9×9 grid. Faster = higher score.", color: "var(--bq-accent)" },
  { key: "word_building", path: "/word", icon: Type, title: "Word Building", desc: "Unscramble today's letters — one guess, answer revealed later.", color: "var(--bq-accent2)" },
  { key: "trivia", path: "/trivia", icon: HelpCircle, title: "Trivia of the Day", desc: "One question, four answers. First-3 correct snag a bonus.", color: "var(--bq-accent)" },
  { key: "caption", path: "/caption", icon: Camera, title: "Photo Caption", desc: "Drop your wittiest caption on today's photo. Most votes wins.", color: "var(--bq-danger)" },
  { key: "pictionary", path: "/pictionary", icon: Pencil, title: "Office Pictionary", desc: "Real-time drawing rooms. 2-8 players, fastest guesser wins.", color: "var(--bq-accent)" },
  { key: "departments", path: "/departments", icon: Building2, title: "Department Battles", desc: "Live standings of every department. Win bragging rights.", color: "var(--bq-accent2)", alwaysOn: true },
];

export default function HomeHub({ me, info, token, onAvatar, headerExtras }) {
  const navigate = useNavigate();
  const enabled = info?.games_enabled || {};
  const visibleGames = GAMES.filter((g) => g.alwaysOn || enabled[g.key] !== false);

  const goWithToken = (path) => navigate(path);

  const initials = (me?.name || "??").split(" ").map((w) => w[0]).join("").slice(0,2).toUpperCase();

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-6 py-4 flex flex-wrap items-center justify-between gap-3 themed-border" style={{background: "var(--bq-header-bg)"}}>
        <BrandLogo size="md" title={info?.name || "TekWissen Fun Zone"} subtitle="Where work meets play" />
        <div className="flex items-center gap-3">
          <button
            data-testid="hub-shop-link"
            onClick={() => navigate("/shop")}
            className="flex items-center gap-1 px-3 py-2 border-2 themed-border text-xs font-black uppercase themed-shadow-sm"
            style={{background: "var(--bq-accent)", color: "var(--bq-text)"}}
            title="Merch Store"
          >
            <Store size={14}/> <span className="hidden sm:inline">Merch Store</span>
          </button>
          {me?.is_demo && (
            <button
              data-testid="hub-admin-link"
              onClick={() => navigate("/admin")}
              className="hidden sm:flex items-center gap-1 px-3 py-2 border-2 themed-border text-xs font-black uppercase"
              style={{background: "var(--bq-card-bg)"}}
              title="Admin Login"
            >
              <LogIn size={14}/> Admin
            </button>
          )}
          {headerExtras}
          <div className="flex items-center gap-2 px-3 py-1 border-2 themed-border" style={{background: "var(--bq-card-bg)"}}>
            {me?.avatar_path ? (
              <img src={`${API}/files/${me.avatar_path}`} alt="" className="w-9 h-9 object-cover border-2 themed-border"/>
            ) : me?.avatar ? (
              <img src={me.avatar} alt="" className="w-9 h-9 object-cover border-2 themed-border"/>
            ) : (
              <div className="w-9 h-9 flex items-center justify-center font-black text-sm border-2 themed-border" style={{background: "var(--bq-accent)", color: "var(--bq-text)"}}>{initials}</div>
            )}
            <div className="hidden sm:block">
              <div className="font-black uppercase text-sm">{me?.name}</div>
              <div className="text-xs font-mono-bq" style={{color: "var(--bq-muted)"}}>{me?.employee_id} · {me?.is_demo ? "DEMO" : "ATS"}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="text-sm uppercase tracking-widest font-mono-bq" style={{color: "var(--bq-muted)"}}>Welcome back</div>
            <h2 className="text-4xl md:text-5xl font-black uppercase">Hi, {me?.name?.split(" ")[0] || "Player"} <span className="font-graffiti" style={{color: "var(--bq-accent)", WebkitTextStroke:"2px var(--bq-text)"}}>let's play</span></h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="border-2 themed-border px-3 py-1 themed-shadow-sm flex items-center gap-1" style={{background: "var(--bq-accent2)", color: "var(--bq-text)"}}>
              <Flame size={16}/><span className="font-black">{me?.streak ?? 0}</span><span className="text-xs">streak</span>
            </div>
            <div className="brutal-card px-3 py-1 themed-shadow-sm flex items-center gap-1">
              <Award size={16}/><span className="font-black">{me?.points ?? 0}</span><span className="text-xs">pts</span>
            </div>
          </div>
        </div>

        {me?.game_streak && (
          <div className="mb-8 border-2 themed-border themed-shadow-sm p-4 flex items-center gap-3" style={{background: "var(--bq-accent2)", color: "var(--bq-text)"}} data-testid="streak-nudge">
            <div className="w-11 h-11 border-2 themed-border flex items-center justify-center flex-shrink-0" style={{background: "var(--bq-card-bg)"}}>
              <Flame size={22} style={{color: "#FF6A00"}}/>
            </div>
            {me.game_streak.streak > 0 ? (
              <div>
                <div className="font-black uppercase" data-testid="streak-nudge-count">{me.game_streak.streak}-day win streak!</div>
                <div className="text-sm">Win all 3 games <strong>{me.game_streak.to_next}</strong> more {me.game_streak.to_next === 1 ? "day" : "days"} in a row to earn a <strong>+50 bonus</strong>.</div>
              </div>
            ) : (
              <div>
                <div className="font-black uppercase">Start a win streak!</div>
                <div className="text-sm">Solve all 3 games today (Rebus, Sudoku &amp; Word) for a <strong>+10 bonus</strong> — reach a 5-day streak for <strong>+50</strong>.</div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visibleGames.map((g) => (
            <button
              key={g.key}
              data-testid={`game-${g.key}`}
              onClick={() => goWithToken(g.path)}
              className="brutal-card p-6 text-left transition-all hover:translate-y-[-4px] hover:themed-shadow"
            >
              <div className="w-14 h-14 border-2 themed-border flex items-center justify-center mb-3" style={{background: g.color}}>
                <g.icon size={28} style={{color: "var(--bq-text)"}}/>
              </div>
              <h3 className="text-2xl font-black uppercase tracking-tight">{g.title}</h3>
              <p className="mt-2 text-sm" style={{color: "var(--bq-muted)"}}>{g.desc}</p>
              <div className="mt-4 text-xs uppercase font-mono-bq tracking-widest flex items-center gap-1">
                Play now <Zap size={12}/>
              </div>
            </button>
          ))}
        </div>

        <HubLeaderboard token={token} me={me} />

        <div className="brutal-card p-6 mt-10">
          <h3 className="text-xl font-black uppercase mb-2 flex items-center gap-2"><Sparkles size={20}/> What's New</h3>
          <p className="text-sm" style={{color: "var(--bq-muted)"}}>All six games are live. Drop suggestions in #funzone for the next one.</p>
        </div>
      </main>
    </div>
  );
}
